import { useState } from "react";

export type PersonNote = { text: string; suspect: boolean };
type Notes = { version: 1; tonight: string; people: Record<string, PersonNote> };
const emptyNotes = (): Notes => ({ version: 1, tonight: "", people: {} });
export const notesKey = (gameId: string, playerId: string) =>
  `clue-dossier-notes-v1:${JSON.stringify([gameId, playerId])}`;

function readNotes(key: string): Notes {
  const raw = localStorage.getItem(key);
  if (!raw) return emptyNotes();
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object" || !("version" in parsed) || parsed.version !== 1 ||
      !("tonight" in parsed) || typeof parsed.tonight !== "string" ||
      !("people" in parsed) || !parsed.people || typeof parsed.people !== "object" || Array.isArray(parsed.people)) {
    throw new Error("Unreadable notes");
  }
  const people: Record<string, PersonNote> = {};
  for (const [name, note] of Object.entries(parsed.people)) {
    if (!note || typeof note !== "object" || typeof note.text !== "string" || typeof note.suspect !== "boolean") {
      throw new Error("Unreadable person note");
    }
    Object.defineProperty(people, name, { value: { text: note.text, suspect: note.suspect }, enumerable: true, writable: true, configurable: true });
  }
  return { version: 1, tonight: parsed.tonight, people };
}

/* UI-DESIGN: game + player scope is explicit, never inferred from a possibly stale
   game in Redux. The caller remounts on scope changes so another dossier cannot
   briefly display or overwrite these notes. Saves are synchronous before navigation. */
export function useDossierNotes(gameId: string | null, playerId: string) {
  const key = gameId && playerId ? notesKey(gameId, playerId) : null;
  const [initial] = useState(() => {
    if (!key) return { notes: emptyNotes(), failed: false };
    try { return { notes: readNotes(key), failed: false }; }
    catch { return { notes: emptyNotes(), failed: true }; }
  });
  const [notes, setNotes] = useState(initial.notes);
  const [failed, setFailed] = useState(initial.failed);
  const [saved, setSaved] = useState(false);

  const change = (update: (current: Notes) => Notes) => {
    if (!key) return;
    // Preserve edits in other tabs/fields; a failed read never overwrites stored data.
    try {
      const latest = readNotes(key);
      const next = update(failed ? notes : latest);
      localStorage.setItem(key, JSON.stringify(next));
      setNotes(next);
      setFailed(false);
      setSaved(true);
    } catch {
      setNotes(update(notes));
      setFailed(true);
      setSaved(false);
    }
  };

  return {
    notes,
    enabled: !!key,
    failed,
    status: !key ? "Open your dossier from the game to keep notes for this night."
      : failed ? "Notes aren’t saved. Keep this page open and copy your text before leaving."
      : saved ? "Saved on this browser only. Not sent to your host."
      : "Autosaves on this browser only. Not sent to your host.",
    setTonight: (text: string) => change(current => ({ ...current, tonight: text })),
    setPerson: (name: string, patch: Partial<PersonNote>) => change(current => ({
      ...current,
      people: { ...current.people, [name]: { text: "", suspect: false, ...current.people[name], ...patch } },
    })),
  };
}
export type DossierNotes = ReturnType<typeof useDossierNotes>;
